/* Eloss — quantity stepper
 *
 * Spec: §F.2 item 8, §N (target sizes, live regions), §P.5.
 *
 * The <input type="number"> is the control; this element only adds the two
 * buttons. With JavaScript unavailable the native spinner and direct typing
 * both still work and still submit, so nothing purchase-critical depends on
 * this file (§P.5).
 *
 * The `max` attribute is present only when Shopify actually tracks inventory
 * for the variant and denies overselling. On this catalogue it is normally
 * absent, so clamping is a no-op — correct, not missing.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  Eloss.define(
    'eloss-quantity',
    class ElossQuantity extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.quantity__input');
        if (!this.input) return;

        this.buttons = Array.from(this.querySelectorAll('[data-quantity-step]'));
        this.limitNote = this.querySelector('[data-quantity-limit]');

        this.onClick = (event) => this.step(event.currentTarget);
        this.onInput = () => this.clamp();

        this.buttons.forEach((button) =>
          button.addEventListener('click', this.onClick)
        );
        this.input.addEventListener('change', this.onInput);

        this.sync();
      }

      disconnectedCallback() {
        this.buttons?.forEach((button) =>
          button.removeEventListener('click', this.onClick)
        );
        this.input?.removeEventListener('change', this.onInput);
        this.buttons = null;
      }

      get min() {
        return Number(this.input.min) || 1;
      }

      get max() {
        const raw = this.input.max;
        return raw === '' || raw === null ? Infinity : Number(raw);
      }

      step(button) {
        const delta = Number(button.dataset.quantityStep) || 0;
        const next = (Number(this.input.value) || this.min) + delta;
        const clamped = String(Math.min(Math.max(next, this.min), this.max));
        if (clamped === this.input.value) return;
        this.input.value = clamped;
        this.sync();

        /* Setting .value in script does not fire `change`, so anything watching
           the input would never hear a button press. On the product page
           nothing listens — the value is simply read at submit — but a cart
           line's quantity IS the mutation trigger (§H.1), so the control has to
           announce its own change. Phase 3 behaviour is unaffected: an extra
           event nobody subscribes to changes nothing. */
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      clamp() {
        const value = Number(this.input.value);
        if (Number.isNaN(value)) {
          this.input.value = String(this.min);
        } else {
          this.input.value = String(Math.min(Math.max(value, this.min), this.max));
        }
        this.sync();
      }

      /** Reflect the ceiling on the controls and in the note, if there is one. */
      sync() {
        const value = Number(this.input.value) || this.min;
        const atMax = value >= this.max;

        this.buttons.forEach((button) => {
          const delta = Number(button.dataset.quantityStep) || 0;
          button.disabled =
            (delta < 0 && value <= this.min) || (delta > 0 && atMax);
        });

        if (this.limitNote) this.limitNote.hidden = !atMax;
      }
    }
  );
})();
